import { useEffect, useRef, useState } from 'react';
import { usePooledVideo } from '../../utils/useVideoPool';

interface VideoBackgroundProps {
  src: string;
  isActive: boolean;
  isNearby?: boolean; // For preloading adjacent slides
}

export const VideoBackground = ({ src, isActive, isNearby = false }: VideoBackgroundProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  // Track which element has loaded, so a new pooled element starts unloaded
  const [loadedVideo, setLoadedVideo] = useState<HTMLVideoElement | null>(null);

  // Use pooled video when active or nearby
  const shouldUsePool = isActive || isNearby;
  const pooledVideo = usePooledVideo(shouldUsePool ? src : undefined, shouldUsePool);
  const isLoaded = pooledVideo !== null && loadedVideo === pooledVideo;

  // Handle video attachment to DOM and load tracking
  useEffect(() => {
    if (!pooledVideo || !containerRef.current) return;

    // Attach video to DOM if not already attached
    if (pooledVideo.parentElement !== containerRef.current) {
      containerRef.current.appendChild(pooledVideo);
    }

    const handleLoadedData = () => {
      setLoadedVideo(pooledVideo);
    };

    const handleError = (e: Event) => {
      console.error('Video loading error:', e);
    };

    pooledVideo.addEventListener('loadeddata', handleLoadedData);
    pooledVideo.addEventListener('error', handleError);

    // Pooled or preloaded elements may have fired loadeddata before we subscribed
    if (pooledVideo.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      handleLoadedData();
    }

    return () => {
      pooledVideo.removeEventListener('loadeddata', handleLoadedData);
      pooledVideo.removeEventListener('error', handleError);
      // Don't remove from DOM here - let the pool manage it
    };
  }, [pooledVideo]);

  // Play/pause based on active state
  useEffect(() => {
    if (!pooledVideo) return;

    if (!isActive) {
      pooledVideo.pause();
      return;
    }

    if (isLoaded) {
      pooledVideo.play().catch(console.error);
    }
  }, [pooledVideo, isActive, isLoaded]);

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 w-full h-full overflow-hidden"
      style={{ display: isActive ? 'block' : 'none' }}
    />
  );
};
